import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { CommanSubscribeService } from "./comman-subscribe.service";
import { AppService } from "./app.service";
import { API } from "../constants";
import { applyMessageEdit, EDIT_ACTION_TYPE } from '../utils/message-edit.util';

@Injectable({
  providedIn: "root",
})
export class PubNubService {
  pubnub: any;
  userId: any;
  unreadChannels$ = new BehaviorSubject<{ [key: string]: number }>({});
  private joinedChannelsSubject = new BehaviorSubject<any[]>([]);
  joinedChannels$ = this.joinedChannelsSubject.asObservable();
  // Fires every time init()'s keyset subscription (re)constructs this.pubnub
  // with a fresh client - callers that fetched membership-derived data
  // (e.g. community-sidenav's "Joined"/"Trending" lists) before the real org
  // keyset replaced the placeholder one need to know to re-fetch, since
  // nothing else tells them the client underneath them just changed.
  keysetReady$ = new Subject<void>();
  pubnubSubscription: Subscription;
  private refreshTimer: any;
  private messageListener: any;
  private accessDeniedListener: any;
  private accessDeniedRetried = new Set<string>();
  private refreshInFlightCallbacks: Array<(success: boolean) => void> | null = null;
  constructor(private toastrService: ToastrService,
    private commonSubscribeService: CommanSubscribeService,
    private appService: AppService,
  ) { }
  private channelMembersSubject = new BehaviorSubject<any[]>([]);
  channelMembers$ = this.channelMembersSubject.asObservable();

  init() {
    if (!this.pubnub) {
      const isDynamicEnabled = environment.DYNAMIC_PUBNUB_ENABLED;
      const PubNubClass = (window as any).PubNub;

      if (isDynamicEnabled) {
        // Dynamic mode
        this.pubnubSubscription = this.commonSubscribeService.getPubnubkeysetInfo().subscribe((response) => {
          let parsedResponse: any;
          try {
            parsedResponse = typeof response === 'string' ? JSON.parse(response) : response;
          } catch (error) {
            console.error('Failed to parse PubNub keyset response:', error);
            parsedResponse = null;
          }
          const publishKey = parsedResponse?.pubnub_publish_key;
          const subscribeKey = parsedResponse?.pubnub_subscribe_key;

          this.pubnub = new PubNubClass({
            publishKey: publishKey || environment.pubNub.publishKey,
            subscribeKey: subscribeKey || environment.pubNub.subscribeKey,
            uuid: 'sample-uuid',
          });

          // This callback re-fires every time the fan's active org/keyset
          // changes (e.g. browsing a different partner's public channels),
          // silently replacing this.pubnub with a fresh client still on the
          // placeholder uuid - this.userId (separate bookkeeping) keeps its
          // already-real value from before, so any guard comparing against
          // it alone (e.g. getMemberships()'s this.userId !== userId check)
          // sees no change and never re-syncs the new client, leaving it
          // stuck on 'sample-uuid' until something coincidentally calls
          // connect() with a different id than last time.
          if (this.userId && this.userId !== 'sample-uuid') {
            this.pubnub.setUUID(this.userId);
          }

          console.log("Using dynamic PubNub keys from service:");
          console.log({ publishKey:publishKey || environment.pubNub.publishKey, subscribeKey:subscribeKey || environment.pubNub.subscribeKey });

          // Only signal once we have the real org keyset, not the very
          // first emission (this subject is seeded with null before the
          // real one arrives) - re-fetching against still-placeholder keys
          // would just fail again for no benefit.
          if (publishKey && subscribeKey) {
            this.keysetReady$.next();
          }
        });
      } else {
        //  Static fallback mode
        const config = {
          publishKey: environment.pubNub.publishKey,
          subscribeKey: environment.pubNub.subscribeKey,
          uuid: 'sample-uuid',
        };

        this.pubnub = new PubNubClass(config);
      }
    }
  }

  connect(id, callback) {
    try {
      const cachedToken = localStorage.getItem('pubnub_token');
      if (cachedToken) {
        this.pubnub.setToken(cachedToken);
      }
      id = (id && id.toString().includes('fan_')) ? id : 'fan_' + id;
      if (this.userId !== id) {
        this.userId = id;
        this.pubnub.setUUID(id);
      }
      // Tokens carry a fixed TTL (~240 min) and are otherwise only refreshed
      // on 3 narrow actions (create/approve/join) - a session left open
      // longer than that with none of those actions would silently start
      // 403ing ("Token is expired") on every PAM-gated call. Proactively
      // refresh well within the TTL so an active session never gets there.
      if (!this.refreshTimer) {
        // Read this.userId (not the closed-over id param) so this keeps
        // refreshing the CURRENT identity even if this singleton service
        // is later reused for a different login without a full page reload.
        this.refreshTimer = setInterval(() => this.refreshToken(this.userId), 60 * 60 * 1000);
      }

      // Checked on every connect() call, not just the first - a tab that's
      // been backgrounded or asleep for hours can miss every tick of the
      // interval above (browsers throttle/suspend timers in inactive tabs),
      // so a session that's been idle a while has no other chance to notice
      // its token went stale until the user switches to a different space
      // and this runs again.
      //
      // The refresh is best-effort: orgs whose keyset never had Access
      // Manager enabled (e.g. older orgs) have no working token to
      // refresh, and generateToken() legitimately fails for them - but
      // channel access never depended on a valid token for those orgs
      // in the first place, so a failed refresh must still let the
      // caller proceed, exactly as connect() always did before this
      // check existed.
      if (this.isTokenExpiredOrNearExpiry(cachedToken)) {
        this.refreshToken(id, () => callback(true));
        return;
      }
      callback(true);
    } catch (e) {
      console.error("Error while connecting to the channel", e);
      callback(false, e);
    }
  }

  private isTokenExpiredOrNearExpiry(token: string | null) {
    if (!token) return false;
    try {
      const parsed = this.pubnub.parseToken(token);
      const expirySeconds = parsed.timestamp + parsed.ttl * 60;
      const nowSeconds = Math.floor(Date.now() / 1000);
      return expirySeconds - nowSeconds < 5 * 60;
    } catch (e) {
      return true;
    }
  }

  // A token minted earlier only grants access to channels the user was
  // already a member of at that time. Call this after any action that
  // changes the current user's own channel memberships (e.g. creating or
  // being approved into a channel) so the client picks up a token that
  // actually covers the new channel, instead of 403ing on a stale one.
  refreshToken(pubNubUserId: string, callback?: (success: boolean) => void) {
    this.appService.postRequest(API.GET_PUBNUB_TOKEN, { user_id: pubNubUserId }).subscribe(
      (result: any) => {
        const token = result?.pubnub_token?.data;
        if (result?.pubnub_token?.status === 200 && token) {
          localStorage.setItem('pubnub_token', token);
          if (this.pubnub) {
            this.pubnub.setToken(token);
          }
          callback?.(true);
        } else {
          callback?.(false);
        }
      },
      (error: any) => {
        console.error('Failed to refresh PubNub token:', error);
        callback?.(false);
      }
    );
  }

  // setToken() swaps the ENTIRE client's auth token, not per-channel - if
  // several channels get denied in the same batched subscribe (the normal
  // case, e.g. multiple ungranted channels at once), firing one refreshToken
  // call per channel means several concurrent GET_PUBNUB_TOKEN responses
  // race to setToken() the client, which can leave it in an inconsistent
  // state and break every channel, not just the originally denied ones.
  // Route all concurrent callers through a single in-flight refresh instead.
  private refreshTokenSerialized(callback: (success: boolean) => void) {
    if (this.refreshInFlightCallbacks) {
      this.refreshInFlightCallbacks.push(callback);
      return;
    }
    this.refreshInFlightCallbacks = [callback];
    this.refreshToken(this.userId, (success) => {
      const callbacks = this.refreshInFlightCallbacks || [];
      this.refreshInFlightCallbacks = null;
      callbacks.forEach((cb) => cb(success));
    });
  }

  // Thread channels are brand-new, synthetic channel names created on the
  // fly (main channel + message id) - unlike the main channel, nothing ever
  // registers PubNub membership for them, so generateToken()'s grant scan
  // (which only ever grants channels the user has a membership record for)
  // can never include them, and publishing into one always gets denied.
  // Mirrors the existing, already-working setMemberships() pattern used for
  // joining the main community channel.
  async joinThreadChannel(channelUrl: string): Promise<boolean> {
    return this.joinChannelById(channelUrl, 'thread channel');
  }

  // Event/stream channels (naming pattern `{env}_{orgId}_{channelId}_...`)
  // don't encode public/private in their name the way community channels
  // do, so generateToken()'s regex pattern-grant never covers them - the
  // only way a fan's token ever includes one is an explicit membership
  // record, which nothing was creating before a fan's first enterChannel()
  // call. Call this before enterChannel() for any event/stream channel.
  async joinEventChannel(channelUrl: string): Promise<boolean> {
    return this.joinChannelById(channelUrl, 'event channel');
  }

  // Same retry-once-after-refresh pattern as enterChannel()/getMemberships():
  // a stale or expired token (or one minted before connect() ever fetched
  // one) makes App Context writes 403 even though a fresh token would allow them.
  private async withAccessRetry(call: () => Promise<any>): Promise<any> {
    try {
      return await call();
    } catch (error: any) {
      const status = error?.status;
      const denied = status?.category === 'PNAccessDeniedCategory' || status?.statusCode === 403;
      if (!denied) throw error;
      const refreshed = await new Promise<boolean>((resolve) => this.refreshTokenSerialized(resolve));
      if (!refreshed) throw error;
      return call();
    }
  }

  private async joinChannelById(channelUrl: string, label: string): Promise<boolean> {
    try {
      await this.withAccessRetry(() => this.pubnub.objects.setMemberships({
        uuid: this.userId,
        channels: [{ id: channelUrl, status: 'active' }],
      }));
      return true;
    } catch (error) {
      console.error(`Failed to join ${label}:`, error);
      return false;
    }
  }

  enterGroupChannel(userIds: any[]) {
    return true;
  }

  enterChannel(channelName, trial = 1, callback, refreshedOnce = false) {
    trial = trial || 1;
    // No cached-token pre-check here: a missing token doesn't mean access
    // is denied (e.g. an org whose keyset never had Access Manager enabled
    // legitimately has no token and needs none) - let the real subscribe
    // attempt below run, and rely on its own PNAccessDeniedCategory check
    // to report a genuine denial.
    if (!this.pubnub.getSubscribedChannels().includes(channelName)) {
      const subscribeToChannel = () => {
        this.pubnub.subscribe({
          channels: [channelName],
        });
        this.pubnub.addListener({
          status: (statusEvent) => {
            if (
              statusEvent.category === "PNAccessDeniedCategory" &&
              statusEvent.affectedChannels?.includes(channelName)
            ) {
              // A grant created by someone else's action (e.g. this user was
              // just added to a private channel) doesn't reach this client's
              // own token until its next refresh - retry once after an
              // explicit refresh before treating this as a genuine denial.
              if (!refreshedOnce) {
                this.refreshToken(this.userId, (success) => {
                  if (success) {
                    // subscribe() already registered this channel in the
                    // SDK's own tracked set the moment it was called,
                    // regardless of the server denying it - without
                    // dropping it first, the guard above would see it as
                    // "already subscribed" and skip straight to
                    // callback(channelName), reporting fake success
                    // without ever retrying with the refreshed token.
                    this.pubnub.unsubscribe({ channels: [channelName] });
                    this.enterChannel(channelName, 1, callback, true);
                  } else {
                    console.error(`Access Denied to channel: ${channelName}. Check PAM token or permissions.`);
                    this.toastrService.error(`Access Denied to channel: ${channelName}. Check PAM token or permissions.`);
                    callback(false, {
                      type: 'accessDenied',
                      message: `Access denied for channel: ${channelName}`,
                      details: statusEvent,
                    });
                  }
                });
                return;
              }
              console.error(`Access Denied to channel: ${channelName}. Check PAM token or permissions.`);
              this.toastrService.error(`Access Denied to channel: ${channelName}. Check PAM token or permissions.`);
              callback(false, {
                type: 'accessDenied',
                message: `Access denied for channel: ${channelName}`,
                details: statusEvent,
              });
              return ;
            } else if (
              statusEvent.category === "PNConnectedCategory" &&
              statusEvent.affectedChannels.includes(channelName)
            ) {
              console.log("Successfully entered the channel:", channelName);
              callback(channelName, null);
            } else if (statusEvent.error) {
              if (trial <= 2) {
                subscribeToChannel();
                trial++;
              } else {
                callback(false, statusEvent.error);
              }
            }
          },
        });
      };
      subscribeToChannel();
    } else {
      callback(channelName, null);
    }
  }

  disconnect(channels: string[]) {
    if (this.pubnub) {
      // removeListener() requires the exact listener object passed to
      // addListener() - without removing it here, the message listener
      // from subscribeToChannels() keeps firing indefinitely, even after
      // this.userId is reset to null below.
      if (this.messageListener) {
        this.pubnub.removeListener(this.messageListener);
        this.messageListener = null;
      }
      if (this.accessDeniedListener) {
        this.pubnub.removeListener(this.accessDeniedListener);
        this.accessDeniedListener = null;
      }
      this.accessDeniedRetried.clear();
      this.pubnub.unsubscribeAll({ channels: channels });
      this.userId = null;
      console.log("Disconnected from all channels.");
    }
  }

  reconnect(channelName, callback: (status: any) => void) {
    this.pubnub.reconnect();
    this.pubnub.subscribe({ channels: [channelName] });
    this.pubnub.addListener({
      status: (status) => callback(status),
    });
  }
  receiveReactions(channel: string, listener: (reaction: any) => void) {
    try {
      this.pubnub.addListener({
        messageAction: (msg) => {
          console.log("Received reaction:", msg, msg.channel, channel);
          if (msg.channel === channel) {
            listener(msg);
          }
        },
        error: (error) => {
          console.error('Error while retrieving receiveReactions:', error);
        }
      });
    } catch (error) {
      console.error('Error while retrieving reactions:', error);
    }
  }


  markThreadStarted(
    channel: string,
    messageTimetoken: number,
    messageId: string,
    userId: string,
    callback: (status: any, response: any) => void
  ) {
    try {
      const action = {
        type: "thread",
        value: JSON.stringify({
          messageId,
          userId,
          timestamp: Date.now()
        })
      };
      // Add message action to indicate thread has started
      this.pubnub.addMessageAction({
        channel,
        messageTimetoken,
        action
      }, callback);
    } catch (err) {
      console.error("Error in markThreadStarted:", err);
      callback({ error: true, message: err.message }, null);
    }
  }

  sendMessageReaction(channel: string, messageTimetoken: number, reactionVal: string, userDetails: any, callback: (status: any, response: any) => void) {
    try {
      const reactionData = {
        type: "reaction",
        value: JSON.stringify({
          emoji: reactionVal,
          userId: userDetails.userId,
          nickname: userDetails.nickname,
          messageId: userDetails.messageId,
          profileUrl: userDetails.profileUrl,
          timestamp: Date.now(),
        })
      };

      const data = {
        channel: channel,
        messageTimetoken: messageTimetoken,
        action: reactionData,
        pn_gcm: {
          'notification': {
            'title': 'channel name',
            'body': reactionVal,
          },
          'data': {
            'channel_id': channel,
            'type': "reaction",
            'user_id': userDetails.userId
          },
        },
      };
      console.log("dataaa.......", data)
      this.pubnub.getMessageActions({ channel, messageTimetoken }, (status, response) => {
        if (status.error) {
          console.error('Error while fetching reactions:', status.error);
          return callback(status, null);
        }

        const existingReactions = response.data.filter(reaction => {
          const reactionValue = JSON.parse(reaction.value);
          return reactionValue.userId === userDetails.userId && reaction.messageTimetoken == messageTimetoken && reaction.type === 'reaction';
        });

        const sameReaction = response.data.some(reaction => {
          const reactionValue = JSON.parse(reaction.value);
          return reactionValue.emoji === reactionVal && reaction.messageTimetoken == messageTimetoken && reaction.type === 'reaction';
        });

        if (existingReactions.length > 0) {
          // Remove the existing reaction
          this.pubnub.removeMessageAction({ channel, messageTimetoken, actionTimetoken: existingReactions[0].actionTimetoken }, (removeStatus) => {
            if (removeStatus.error || sameReaction) {
              return callback(removeStatus, null);
            }
            // Add the new reaction after removing the old one
            this.pubnub.addMessageAction(data, callback);
          });
        } else {
          // Add a new reaction
          this.pubnub.addMessageAction(data, callback);
        }
      });
    } catch (error) {
      console.error('Error while sending a reaction:', error);
      callback({ error: true, message: error.message }, null);
    }
  }


  sendMessage(
    channelName: string,
    message: {},
    callback: (status: any, response: any) => void
  ) {
    console.log("sendMessage-channelName",channelName);
    console.log("sendMessage-message",message)
    this.pubnub.publish(
      {
        channel: channelName,
        message: message,
      },
      (status, response) => callback(status, { ...response, message })
    );
  }
  receiveMessages(channel: string, listener: (message: any) => void) {
    try {
      this.pubnub.addListener({
        message: (msg) => {
          if (msg.subscribedChannel == channel) {
            listener(msg);
          }
        },
      });
    } catch (error) {
      console.error("Error while fetching messages", error);
    }
  }

  // Shared by every history-fetch method's error branch: a grant created by
  // someone else's action doesn't reach this client's own token until its
  // next refresh - retry once after an explicit refresh before giving up.
  // Returns true if a retry was kicked off (caller should return without
  // reporting failure yet), false if the caller should proceed to its own
  // failure handling.
  private retryHistoryOnAccessDenied(
    status: any,
    refreshedOnce: boolean,
    retry: () => void,
    giveUp: () => void
  ): boolean {
    if (status.category !== 'PNAccessDeniedCategory' || refreshedOnce) {
      return false;
    }
    this.refreshToken(this.userId, (success) => {
      if (success) {
        retry();
      } else {
        giveUp();
      }
    });
    return true;
  }

  getAllMessages(
    channelName: string,
    callback: (messages: any, status: any) => void,
    refreshedOnce = false
  ) {
    const allMessages: any[] = [];
    let startTimeToken: number | null = null;
    const fetchHistory = () => {
      this.pubnub.history(
        {
          channel: channelName,
          count: 100,
          start: startTimeToken || undefined,
        },
        (status, response) => {
          if (status.error) {
            const retrying = this.retryHistoryOnAccessDenied(
              status,
              refreshedOnce,
              () => this.getAllMessages(channelName, callback, true),
              () => callback([], status)
            );
            if (retrying) return;
            console.log("Error while fetching messages:", status);
            callback([], status);
          } else {
            const messages = response.messages;
            allMessages.unshift(...messages);

            // If we have more messages to fetch
            if (messages.length === 100) {
              // Update startTimeToken to fetch older messages
              startTimeToken = messages[0].timetoken;
              // callback(allMessages, status);
              fetchHistory(); // Fetch the next batch
            } else {
              // We've fetched all messages
              // callback(allMessages, status);


              this.fetchReactions(channelName, allMessages, (updatedMessages) => {
                callback(updatedMessages, status); // Return final structured data
              });
            }
          }
        }
      );
    };
    fetchHistory();
  }


  getAllMessage(channel: string, messageCount: number, callback: (messages: any[], status: any) => void, refreshedOnce = false) {
    const allMessages: any[] = [];
    let startTimeToken: number | null = null;

    const fetchMessages = () => {
      this.pubnub.history(
        {
          channel: channel,
          count: messageCount, // Maximum number of messages per request
          start: startTimeToken || undefined // Start from the last fetched message
        },
        (status, response) => {
          if (status.error) {
            const retrying = this.retryHistoryOnAccessDenied(
              status,
              refreshedOnce,
              () => this.getAllMessage(channel, messageCount, callback, true),
              () => callback([], status)
            );
            if (retrying) return;
            console.log('Error retrieving history:', status);
            callback([], status);
          } else {
            const messages = response.messages;
            allMessages.unshift(...messages);

            if (messages.length === messageCount) {
              startTimeToken = messages[0].timetoken;
              fetchMessages(); // Fetch next batch
            } else {
              // Once all messages are fetched, process reactions
              this.fetchReactions(channel, allMessages, (updatedMessages) => {
                callback(updatedMessages, status); // Return final structured data
              });
            }
          }
        }
      );
    };
    fetchMessages(); // Start fetching
  }
  // Function to fetch reactions for messages
  private fetchReactions(channel: string, messages: any[], callback: (updatedMessages: any[]) => void) {
    const messagePromises = messages.map((msg) =>
      new Promise((resolve) => {
        const messageId = msg.entry?.messageId || msg.messageId;
        const isPoll = msg.entry?.type === 'poll';

        if (isPoll) {
          msg.entry.userVote = {};
          msg.entry.votesPerOption = {};
          msg.entry.totalVotes = 0;
        }

        this.pubnub.getMessageActions(
          { channel: channel, messageTimetoken: msg.timetoken },
          (status, reactionResponse) => {
            if (!status.error) {
              const reactionsMap: { [emoji: string]: { count: number, users: any[] } } = {};

              // ✅ Group reactions by user and track their sequence
              const userReactions: { [userId: string]: Array<{ emoji: string; timestamp: number; userDetails: any }> } = {};

              reactionResponse.data.forEach((reaction) => {
                try {
                  const reactionValue = JSON.parse(reaction.value);
                  if (reactionValue.messageId !== messageId) return;
                  // Edits are not reactions: keep them out of the per-user reaction sequence below
                  if (reaction.type === EDIT_ACTION_TYPE) {
                    applyMessageEdit(msg.entry, reaction.uuid, reactionValue.text, Number(reaction.actionTimetoken));
                    return;
                  }
                  const timestamp = Number(reactionValue.timestamp || reaction.actionTimetoken);
                  const userId = reactionValue.userId;
                  const emoji = reactionValue.emoji;
                  const userDetails = {
                    userId,
                    nickname: reactionValue.nickname,
                    profileUrl: reactionValue.profileUrl,
                    userColor: reactionValue.userColor,
                    shortName: reactionValue.shortName,
                    timestamp
                  };

                  // Store all reactions for this user
                  if (!userReactions[userId]) {
                    userReactions[userId] = [];
                  }
                  userReactions[userId].push({
                    emoji,
                    timestamp,
                    userDetails
                  });

                  // ✅ handle poll logic (keeping existing poll logic)
                  if (reaction.type === 'poll' || isPoll) {
                    const optionId = reactionValue.optionId?.toString();
                    const isMultiple = msg.entry?.isAllowMultipleResponses ?? false;

                    msg.entry.userVote[userId] = msg.entry.userVote[userId] || [];
                    const userVotes = msg.entry.userVote[userId];

                    if (!isMultiple) {
                      const prev = userVotes[0];
                      if (prev && prev !== optionId) {
                        msg.entry.votesPerOption[prev] = (msg.entry.votesPerOption[prev] ?? 1) - 1;
                        msg.entry.totalVotes = (msg.entry.totalVotes ?? 1) - 1;
                      }

                      if (!userVotes.includes(optionId)) {
                        userVotes.length = 0;
                        userVotes.push(optionId);
                        msg.entry.votesPerOption[optionId] = (msg.entry.votesPerOption[optionId] ?? 0) + 1;
                        msg.entry.totalVotes = (msg.entry.totalVotes ?? 0) + 1;
                      }
                    } else {
                      if (userVotes.includes(optionId)) {
                        const index = userVotes.indexOf(optionId);
                        userVotes.splice(index, 1);
                        msg.entry.votesPerOption[optionId] = (msg.entry.votesPerOption[optionId] ?? 1) - 1;
                        msg.entry.totalVotes = (msg.entry.totalVotes ?? 1) - 1;
                      } else {
                        userVotes.push(optionId);
                        msg.entry.votesPerOption[optionId] = (msg.entry.votesPerOption[optionId] ?? 0) + 1;
                        msg.entry.totalVotes = (msg.entry.totalVotes ?? 0) + 1;
                      }
                    }

                    msg.entry.userVote[userId] = userVotes;
                  }

                  if (reaction.type === 'thread') {
                    msg.isThreadInitiated = true;
                  }

                  if (reaction.type === 'block') {
                    msg.isBlocked = true;
                    msg.actionTimetoken = reaction.actionTimetoken;
                  }
                } catch (error) {
                  console.error("Error parsing reaction value:", error);
                }
              });

              // ✅ Process user reactions following the same logic as handleLocalReactionUpdate
              for (const userId in userReactions) {
                // Sort reactions by timestamp to get chronological order
                const sortedReactions = userReactions[userId].sort((a, b) => a.timestamp - b.timestamp);

                let currentActiveEmoji: string | null = null;
                let currentUserDetails: any = null;

                // Process reactions in sequence, similar to handleLocalReactionUpdate logic
                sortedReactions.forEach(reaction => {
                  const { emoji, userDetails } = reaction;

                  // If user already has an active emoji
                  if (currentActiveEmoji) {
                    // If clicking the same emoji again, remove it (untoggle)
                    if (currentActiveEmoji === emoji) {
                      currentActiveEmoji = null;
                      currentUserDetails = null;
                    } else {
                      // Replace with new emoji
                      currentActiveEmoji = emoji;
                      currentUserDetails = userDetails;
                    }
                  } else {
                    // No active emoji, set this one as active
                    currentActiveEmoji = emoji;
                    currentUserDetails = userDetails;
                  }
                });

                // Add to reactions map only if user has an active emoji
                if (currentActiveEmoji && currentUserDetails) {
                  if (!reactionsMap[currentActiveEmoji]) {
                    reactionsMap[currentActiveEmoji] = { count: 0, users: [] };
                  }
                  reactionsMap[currentActiveEmoji].count += 1;
                  reactionsMap[currentActiveEmoji].users.push(currentUserDetails);
                }
              }

              msg.reactions = reactionsMap;
            }

            resolve(msg);
          }
        );
      })
    );

    Promise.all(messagePromises).then(callback);
  }

  /** Subscribe to all channels and listen for new messages */
  subscribeToChannels(channels: string[]) {
    this.pubnub.subscribe({ channels });
    if (this.messageListener) {
      this.pubnub.removeListener(this.messageListener);
    }
    this.messageListener = {
      message: async (event) => {
        const channel = event.channel;
        const lastReadTimetoken = await this.getLastReadTimetoken(channel);
        if (parseInt(event.message.timestamp) > parseInt(lastReadTimetoken)) {
          const unreadCount = await this.getUnreadCount(channel);
          this.updateUnreadStatus(channel, unreadCount);
        }
      }
    };
    this.pubnub.addListener(this.messageListener);

    // PubNub multiplexes every subscribed channel into ONE combined
    // long-poll request - a single channel this user isn't actually granted
    // makes the WHOLE batch 403 repeatedly, silently breaking unread badges
    // for every OTHER channel too, even ones with no problem of their own.
    // Same "refresh once, then give up on just that channel" pattern as
    // enterChannel() - but here we drop the offending channel from the
    // subscribe set instead of failing the whole call, so the rest keep
    // working.
    if (this.accessDeniedListener) {
      this.pubnub.removeListener(this.accessDeniedListener);
    }
    this.accessDeniedListener = {
      status: (statusEvent) => {
        if (statusEvent.category !== 'PNAccessDeniedCategory') {
          return;
        }
        const affected: string[] = statusEvent.affectedChannels || [];
        affected.forEach((channel) => {
          if (this.accessDeniedRetried.has(channel)) {
            console.error(`Dropping ungranted channel from subscription: ${channel}`);
            this.pubnub.unsubscribe({ channels: [channel] });
            return;
          }
          this.accessDeniedRetried.add(channel);
          this.refreshTokenSerialized((success) => {
            if (!success) {
              console.error(`Dropping ungranted channel from subscription: ${channel}`);
              this.pubnub.unsubscribe({ channels: [channel] });
            }
            // On success, PubNub's own retry/reconnect picks the refreshed
            // token up automatically - if it 403s again, the retried-once
            // check above drops it next time instead of looping forever.
          });
        });
      }
    };
    this.pubnub.addListener(this.accessDeniedListener);
  }

  private async setUUIDMetadata(channel: string): Promise<void> {
    if (!this.userId || this.userId === 'sample-uuid') {
      return;
    }
    try {
      await this.pubnub.objects.setUUIDMetadata({
        uuid: this.userId,
        data: { name: this.userId },
      });
    } catch (error) {
      console.error('Error setting UUID metadata:', error);
    }
  }

  async ensureChannelMetadata(channelId: string): Promise<void> {
    const metadata = {
      channel: channelId,
      data: {
        name: channelId,
        custom: {}
      }
    };

    try {
      const response = await this.pubnub.objects.getChannelMetadata({ channel: channelId });
      if (!response?.data) {
        await this.pubnub.objects.setChannelMetadata(metadata);
      }
    } catch (error) {
      if (error?.status?.statusCode === 404) {
        await this.pubnub.objects.setChannelMetadata(metadata);
      } else {
        console.error(`Error ensuring channel metadata for ${channelId}:`, error);
      }
    }
  }

  async ensureUUIDMetadata(channel: string): Promise<void> {
    try {
      await this.ensureChannelMetadata(channel);
      const response = await this.pubnub.objects.getUUIDMetadata({ uuid: this.userId });
      if (response?.data) {
        return;
      }
      await this.setUUIDMetadata(channel);
    } catch (error) {
      if (error?.status?.statusCode === 404) {
        await this.setUUIDMetadata(channel);
      } else {
        console.error('Error checking UUID metadata:', error);
      }
    }
  }

  async getLastReadTimetoken(channel: string): Promise<string> {
    // Guards against a stale/leftover listener firing (e.g. between a
    // disconnect() and the next connect()) with no real user identity -
    // this uuid-scoped PAM call would always 403 for the placeholder.
    if (!this.userId || this.userId === 'sample-uuid') {
      return '0';
    }
    try {
      await this.ensureUUIDMetadata(channel);
      const res = await this.pubnub.objects.getMemberships({
        uuid: this.userId,
        include: {
          customFields: true,
        },
      });
      const channelMembership = res.data.find(m => m.channel.id === channel);
      return channelMembership?.custom?.lastRead?.toString() || '0';
    } catch (error) {
      console.error('Failed to get membership metadata:', error);
      return '0';
    }
  }

  async updateLastReadTimetoken(channel: string) {
    if (!this.userId || this.userId === 'sample-uuid') {
      return;
    }
    const lastReadTimetoken = Date.now();
    try {
      await this.withAccessRetry(() => this.pubnub.objects.setMemberships({
        uuid: this.userId,
        channels: [
          {
            id: channel,
            custom: {
              lastRead: lastReadTimetoken
            }
          }
        ]
      }));
      this.updateUnreadStatus(channel, 0);
    } catch (error) {
      console.error('Failed to update membership metadata:', error);
    }
  }

  /** Update unread status */
  private updateUnreadStatus(channel: string, unreadCount: number) {
    const updatedStatus = { ...this.unreadChannels$.value, [channel]: unreadCount };
    this.unreadChannels$.next(updatedStatus);
  }

  /** Fetch unread status for multiple channels */
  async initializeUnreadStatus(channels: string[]) {
    const unreadStatus: { [key: string]: number } = {};
    await Promise.all(
      channels.map(async (channel) => {
        const unreadCount = await this.getUnreadCount(channel);
        unreadStatus[channel] = unreadCount;
      })
    );
    this.unreadChannels$.next(unreadStatus);
  }

  /** Fetch unread message count for a channel */
  async getUnreadCount(channel: string): Promise<number> {
    const lastReadTimetoken = await this.getLastReadTimetoken(channel);
    let unreadCount = 0;
    let start = null;

    try {
      do {
        const messages = await this.pubnub.fetchMessages({
          channels: [channel],
          count: 100,
          start: start
        });
        const newMessages = messages.channels[channel]?.filter(msg => {
          // First filter: message timestamp should be greater than lastReadTimetoken
          const isAfterLastRead = msg.message.timestamp > lastReadTimetoken;
          // Second filter: exclude messages sent by current user
          const isNotCurrentUser = msg.message._sender.userId !== this.userId;
          return isAfterLastRead && isNotCurrentUser;
        }) || [];
        unreadCount += newMessages.length;
        start = newMessages.length === 100 ? newMessages[0].message.timestamp : null;
      } while (start);
    } catch (error) {
      console.error('Failed to fetch messages for unread count:', channel, error);
      return 0;
    }
    return unreadCount;
  }

  async getChannelMembers(customMetadata: any, channel: any): Promise<string> {
    console.log("getChannelMembers", customMetadata, channel);
    // Guards against this firing before connect() has ever called setUUID()
    // for this session (e.g. a stray call racing ahead of it) - this
    // uuid-scoped PAM call would always 403 for the placeholder identity.
    if (!this.userId || this.userId === 'sample-uuid') {
      this.channelMembersSubject.next([]);
      return '0';
    }
    try {
      const response = await this.pubnub.objects.getChannelMembers({
        // channel: channel.channelUrl,
        channel: channel.pubnubId,
        include: {
          customFields: true,
          UUIDFields: true,
          UUIDCustomFields: true
        },
        limit: 1000
      });
      console.log("getChannelMembers", response, channel);
      this.channelMembersSubject.next(response);
      return response;
    } catch (error) {
      console.error(`Error fetching metadata for channel:`, error,error.status);
      this.channelMembersSubject.next([]);
      return '0';
    }
  }

  async getChannelMemberCount(channel: any): Promise<any> {
    // Same guard as getChannelMembers() above - avoids a doomed PAM call
    // when this fires before connect() has set the real identity.
    if (!this.userId || this.userId === 'sample-uuid') {
      return 0;
    }
    try {
      const result = await this.pubnub.objects.getChannelMembers({
        channel: channel,
        include: {
          totalCount: true
        }
      });

      return result.totalCount;
    } catch (error) {
      console.error("Error getting memberships:", error);
    }

  }

  async getMemberships(userId: string, refreshedOnce = false): Promise<any[]> {
    try {
      // The SDK auto-fills the request's query-string `uuid` (the PAM identity
      // it authenticates as) from the client's own configured uuid, separate
      // from the `userId` param below (which only affects the path). If this
      // runs before connect() has ever called setUUID() for this session, the
      // client is still on its placeholder identity and PAM rejects the call
      // - sync it here so this works regardless of call order relative to connect().
      if (this.userId !== userId) {
        this.userId = userId;
        this.pubnub.setUUID(userId);
      }
      const result = await this.pubnub.objects.getMemberships({
        userId: userId,
        include: {
          channelFields: true,
          customFields: true
        },
        limit: 100
      });

      return result?.data || [];

    } catch (error) {
      // This is what "Joined" spaces are built from (see
      // community-sidenav.component.ts) - silently returning [] here on a
      // transient failure (e.g. this ran before the real org keyset replaced
      // the placeholder one) previously misclassified every joined space as
      // NOT joined for the rest of the session, since nothing recomputed it
      // afterward. Retry once after a token refresh before giving up, same
      // pattern as enterChannel().
      if (!refreshedOnce) {
        return new Promise((resolve) => {
          this.refreshToken(userId, (success) => {
            if (success) {
              resolve(this.getMemberships(userId, true));
            } else {
              console.error("Error fetching memberships (refresh failed):", error);
              resolve([]);
            }
          });
        });
      }
      console.error("Error fetching memberships:", error);
      return [];
    }
  }

  // silent: true for callers granting membership automatically (e.g. opening
  // a public channel for the first time) rather than in response to the
  // fan's own explicit "Join Space" click - a toast reading "Failed to join
  // the space" makes no sense to someone who never asked to join anything.
  async setMemberships(channelDetails?: any, silent = false): Promise<any[]> {
    if (!this.userId || this.userId === 'sample-uuid') {
      return [];
    }
    try {
      const result = await this.withAccessRetry(() => this.pubnub.objects.setMemberships({
        uuid: this.userId,
        channels: [
          {
            id: channelDetails.pubnubId,
            // Distinguishes "granted automatically so this fan can read/post
            // in a public space without a manual step" from "this fan
            // actually clicked Join" - the sidebar's "Joined" list (see
            // community-sidenav.component.ts) reads this to decide whether
            // a space belongs there, since a PubNub membership existing at
            // all is not the same thing as the fan having chosen to join.
            custom: { autoJoined: silent },
            status: 'active'
          }
        ],
        include: {
          customFields: true
        }
      }));

      this.joinedChannelsSubject.next(result.data);
      return result.data;

    } catch (error) {
      console.error('Error setting PubNub membership:', (error as any)?.status?.statusCode, (error as any)?.status?.category, error);
      if (!silent) {
        this.toastrService.error('Failed to join the space');
      }
      this.joinedChannelsSubject.next([]);
      return [];
    }
  }
  // Leaving undoes an explicit join. Public spaces stay readable afterwards
  // through generateToken()'s public-channel pattern grant.
  async removeMembership(channelId: string): Promise<boolean> {
    if (!this.userId || this.userId === 'sample-uuid') {
      return false;
    }
    try {
      const result = await this.withAccessRetry(() => this.pubnub.objects.removeMemberships({
        uuid: this.userId,
        channels: [channelId],
        include: {
          customFields: true
        }
      }));
      this.joinedChannelsSubject.next(result.data);
      return true;
    } catch (error) {
      console.error('Error removing PubNub membership:', (error as any)?.status?.statusCode, (error as any)?.status?.category, error);
      return false;
    }
  }

  createPoll(
    channel: any,
    data: any,
    callback: (status: any, response: any) => void
  ) {
    console.log("createPoll", data, channel);
    try {
      this.pubnub.publish(
        {
          channel: channel,
          message: data,
        },
        (status, response) => {
          if (status.error) {
            console.error("Error creating poll:", status);
          }
          callback(status, response);
        }
      );
    } catch (error) {
      console.error("Error creating poll:", error);
      callback({ error: true, message: error.message }, null);
    }
  }
  /** Stores an edit of a published message as an 'edit' message action (see message-edit.util). */
  editMessage(channel: string, messageTimetoken: any, messageId: string, text: string, callback: (status: any, response: any) => void) {
    try {
      this.pubnub.addMessageAction({
        channel,
        messageTimetoken,
        action: { type: EDIT_ACTION_TYPE, value: JSON.stringify({ messageId, text, editedAt: Date.now() }) },
      }, callback);
    } catch (error) {
      callback({ error: true, message: error.message }, null);
    }
  }

  sendPollVote(poll: any, channel: any, data: any, callback: (status: any, response: any) => void) {
    console.log("sendPollVote", data, channel, poll);
    const payload = {
      channel: channel,
      messageTimetoken: poll.timetoken,
      action: {
        type: poll.type,
        value: JSON.stringify(data),
      },
    };
    try {
      this.pubnub.addMessageAction(
        payload,
        (status, response) => {
          if (status.error) {
            console.error("Error sending poll vote:", status);
          } else {
            console.log("Poll vote response:", response);
          }
          callback(status, response);
        }
      );
    } catch (error) {
      console.error("Error sending poll vote:", error);
      callback({ error: true, message: error.message }, null);
    }
  }

  // comments-section
  receiveCommentMessages(channel: string, listener: (message: any) => void) {
    try {
      this.pubnub.subscribe({
        channels: [channel],
      });

      this.pubnub.addListener({
        message: (msg) => {
          console.log("receivemessage-msg",msg,"....",channel)
          if (msg.channel === channel || msg.subscribedChannel === channel) {
            listener(msg);
          }
        },
        status: (statusEvent) => {
          console.log("Status event:", statusEvent);
        }

      });
    } catch (error) {
      console.error("Error while fetching messages", error);
    }
  }

  ngOnDestroy() {
    if (this.pubnubSubscription) {
      this.pubnubSubscription.unsubscribe();
    }
  }

}